import { Link } from "react-router-dom";
import type { Topic } from "@/mocks/guaData";
import { formatCount, getCategoryBySlug, getTrendMeta } from "@/utils/content";

interface TopicCardProps {
  topic: Topic;
  layout?: "vertical" | "horizontal";
}

export default function TopicCard({ topic, layout = "vertical" }: TopicCardProps) {
  const category = getCategoryBySlug(topic.categorySlug);
  const trend = getTrendMeta(topic.trend);

  if (layout === "horizontal") {
    return (
      <article className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/70 p-4 transition-colors hover:border-primary-200 sm:flex-row">
        <Link
          to={`/topic/${topic.id}`}
          className="relative block h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44"
        >
          <img
            src={topic.image}
            alt={`${topic.title} - 热门吃瓜话题配图`}
            title={`${topic.title} - 热门吃瓜话题`}
            className="h-full w-full object-top"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {category && (
              <Link
                to={`/category/${category.slug}`}
                className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                {category.name}
              </Link>
            )}
            <span className="text-foreground-500">{topic.publishedAt}</span>
          </div>
          <h3 className="mt-2 text-base font-bold leading-snug text-foreground-950">
            <Link to={`/topic/${topic.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
          <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-eye-line text-sm leading-none" aria-hidden="true" />
              </span>
              {formatCount(topic.reads)}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-chat-3-line text-sm leading-none" aria-hidden="true" />
              </span>
              {formatCount(topic.comments)}
            </span>
            <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${trend.className}`}>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className={`${trend.icon} text-xs leading-none`} aria-hidden="true" />
              </span>
              {trend.text}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100/70 transition-colors hover:border-primary-200">
      <Link to={`/topic/${topic.id}`} className="relative block aspect-[16/9] w-full overflow-hidden bg-background-200">
        <img
          src={topic.image}
          alt={`${topic.title} - 热门吃瓜话题配图`}
          title={`${topic.title} - 热门吃瓜话题`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-[1.03]"
        />
        <span className="absolute left-3 top-3 rounded-full bg-foreground-950/80 px-2.5 py-1 text-xs font-medium text-background-50">
          {formatCount(topic.heat)} 热度
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {category && (
            <Link
              to={`/category/${category.slug}`}
              className="cursor-pointer rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              {category.name}
            </Link>
          )}
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 ${trend.className}`}>
            <span className="flex h-3 w-3 items-center justify-center">
              <i className={`${trend.icon} text-xs leading-none`} aria-hidden="true" />
            </span>
            {trend.text}
          </span>
        </div>
        <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950">
          <Link to={`/topic/${topic.id}`} className="cursor-pointer transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4 text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-eye-line text-sm leading-none" aria-hidden="true" />
              </span>
              {formatCount(topic.reads)}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-chat-3-line text-sm leading-none" aria-hidden="true" />
              </span>
              {formatCount(topic.comments)}
            </span>
          </span>
          <span className="whitespace-nowrap">{topic.publishedAt}</span>
        </div>
      </div>
    </article>
  );
}